/**
 * 이름 유효성 검사 결과
 */
export interface NameValidation {
  isValid: boolean;
  error?: string;
}

export interface NameOptions {
  /** 최소 길이 */
  minLength?: number;
  /** 최대 길이 */
  maxLength?: number;
}

/**
 * 한글이 포함되어 있는지 확인
 */
const hasKorean = (str: string): boolean => {
  return /[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(str);
};

/**
 * 영문이 포함되어 있는지 확인
 */
const hasEnglish = (str: string): boolean => {
  return /[a-zA-Z]/.test(str);
};

/**
 * 완성형 한글이 아닌 자음/모음만 있는지 확인
 */
const hasIncompleteKorean = (str: string): boolean => {
  return /[ㄱ-ㅎㅏ-ㅣ]/.test(str);
};

/**
 * 이름 포맷팅
 * - 한글 입력 시: 한글만 허용 (ㄱ-ㅎ, 가-힣), 공백 제거
 * - 영문 입력 시: 영문자, 공백, 아포스트로피('), 하이픈(-) 허용
 * - 한글과 영어 혼용 불가
 * - 숫자와 기타 특수문자는 모두 자동 제거
 *
 * @param input - 입력된 문자열
 * @returns 포맷팅된 이름
 */
export const formatName = (input: string): string => {
  // 한글이 포함되어 있는 경우
  if (hasKorean(input)) {
    // 한글(완성형 + 자음/모음)만 허용, 영문과 공백 제거
    return input.replace(/[^ㄱ-ㅎㅏ-ㅣ가-힣]/g, '');
  }

  // 영문이 포함되어 있는 경우
  if (hasEnglish(input)) {
    // 영문자, 공백, 아포스트로피('), 하이픈(-) 허용
    return input.replace(/[^a-zA-Z\s'\-]/g, '');
  }

  // 한글도 영문도 없으면 모든 특수문자와 숫자 제거
  return input.replace(/[^ㄱ-ㅎㅏ-ㅣ가-힣a-zA-Z\s'\-]/g, '');
};

/**
 * 이름 유효성 검사
 * @param value - 이름
 * @param options - 검증 옵션
 * @returns 유효성 검사 결과
 *
 * @example
 * validateName('홍길동') // { isValid: true }
 * validateName('김') // { isValid: false, error: '2자 이상 입력해주세요' }
 * validateName('ㄱㅣㅁ') // { isValid: false, error: '완성된 한글 또는 영문을 입력해주세요' }
 * validateName('John Doe') // { isValid: true }
 */
export const validateName = (
  value: string,
  options: NameOptions = { minLength: 2, maxLength: 50 },
): NameValidation => {
  const { minLength = 2, maxLength = 50 } = options;

  if (!value || value.length === 0) {
    return { isValid: false, error: '' };
  }

  const trimmed = value.trim();

  // 완성형 한글 검증 (자음/모음만 있으면 에러)
  if (hasIncompleteKorean(trimmed)) {
    return { isValid: false, error: '완성된 한글 또는 영문을 입력해주세요' };
  }

  if (trimmed.length < minLength) {
    return { isValid: false, error: `${minLength}자 이상 입력해주세요` };
  }

  if (trimmed.length > maxLength) {
    return { isValid: false, error: `${maxLength}자 까지만 입력할 수 있어요` };
  }

  return { isValid: true };
};
